import Image from "next/image";
import Link from "next/link";
import { LuBitcoin } from "react-icons/lu";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import { PreBidForm } from "@/components/app/profile/pre-bid-form";
import { AccountSocialCard } from "@/components/shared/account-social-card";
import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import CountdownTimer from "@/components/shared/countdown-timer";
import DesoAvatar from "@/components/shared/deso-avatar";
import { Metric } from "@/components/shared/metric";
import { TextDivider } from "@/components/shared/text-divider";
import { Button } from "@/components/ui/button";
import type { CoinPropertiesResponse } from "@/hooks/use-coin-properties-query";
import { useTotalSupply } from "@/hooks/use-total-supply";
import {
  FOCUS_TICKER_DISPLAY_NAME,
  FOCUS_TOKEN_DISPLAY_NAME,
  OPENFUND_APP_NAME,
} from "@/utils/constants";
import { focusToUsd, formatUSD } from "@/utils/currency";
import { pluralize } from "@/utils/helpers";
import { cn } from "@/utils/lib";

import { USER_PROFILE_TAB_ITEMS_SUBCATEGORIES } from "../feed/feed-user-wrapper";
import { LearnMore } from "../learn-more-link";
import { CurrentBids } from "./current-bids";
import { usePreLaunchOverviewLogic } from "./use-prelaunch-overview-logic";

interface TokenPreLaunchOverviewProps {
  tokenDetails: CoinPropertiesResponse;
  setActiveTab: (tab: USER_PROFILE_TAB_ITEMS_SUBCATEGORIES) => void;
  publicKey: string;
  focusExchangeRate: number;
}

const MAX_WALLETS_SHOWN = 5;

export const TokenPreLaunchOverview = ({
  tokenDetails,
  setActiveTab,
  publicKey,
  focusExchangeRate,
}: TokenPreLaunchOverviewProps) => {
  const {
    loadingTokenOrderbook,
    totalBidsUsd,
    totalBidsFocus,
    uniqueWallets,
    totalBids,
    username,
    tokenOrderbook,
    coinUserName,
    tokenPublicKey,
  } = usePreLaunchOverviewLogic({
    tokenDetails,
    publicKey,
    focusExchangeRate,
  });

  const { totalSupply, loadingTotalSupply } = useTotalSupply({
    publicKey: publicKey || "",
  });

  return (
    <div>
      <div
        className="
          mb-4 flex w-full flex-row items-center justify-between gap-4
          sm:flex-row
        "
      >
        <div className="flex flex-col">
          <h2 className="font-sans text-lg font-semibold text-muted-foreground">
            Token Launch
          </h2>
          <LearnMore
            className="
              h-auto items-start text-left whitespace-normal
              sm:h-7 sm:whitespace-nowrap
            "
            textClassName="max-w-[150px] sm:max-w-[auto]"
            iconSize={14}
          />
        </div>

        <div className="flex items-center gap-4">
          <Button
            variant="default"
            size="sm"
            onClick={() =>
              setDialog(DIALOG.GetTokensEarly, {
                open: true,
                params: {
                  username: tokenDetails.coinProperties.Profile.Username,
                },
              })
            }
          >
            Get Tokens Early
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div
          className="
            mb-4 flex w-full flex-col items-center justify-center gap-2
            rounded-lg border p-8
          "
        >
          <div className="text-sm">{username} is launching in</div>

          {tokenDetails.coinProperties.RunAt?.[0] && (
            <CountdownTimer
              date={new Date(tokenDetails.coinProperties.RunAt?.[0])}
              onReset={() => {}}
              timestampClassName="text-4xl sm:text-7xl leading-none font-inherit text-red-700 bg-transparent"
            />
          )}

          {coinUserName && (
            <EarlyUniqueBidders
              {...{
                uniqueWallets: uniqueWallets.filter(Boolean),
                totalBidsUsd,
                username: coinUserName ?? "",
              }}
            />
          )}
        </div>

        <div className="mb-4 grid w-full grid-cols-1 gap-4">
          <div className="flex w-full flex-col gap-2">
            <PreLaunchMetrics
              {...{
                totalBidsFocus,
                focusExchangeRate,
                totalBids,
                uniqueWallets: uniqueWallets.filter(Boolean),
              }}
            />
          </div>
        </div>

        <CurrentBids
          orders={[...(tokenOrderbook?.bids || [])]}
          tokenPublicKey={tokenPublicKey ?? ""}
          loading={loadingTokenOrderbook || loadingTotalSupply}
          coinUsername={username}
          totalSupply={totalSupply}
        />

        <TextDivider text="Get Tokens Early" spacing="my-4" />

        <div className="mb-2 flex items-center justify-between">
          <div className="flex flex-row items-center gap-3">
            <DesoAvatar size={40} account={{ username, publicKey }} />

            <div className="flex flex-col">
              <div className="font-sans font-semibold text-muted-foreground">
                Place an Early Bid
              </div>
              <p className="text-xs text-balance text-muted">
                Buy {username} tokens at the lowest possible price.
              </p>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setDialog(DIALOG.WalletTopUp, {
                open: true,
              })
            }
          >
            <LuBitcoin className="mr-2 size-4 text-muted" />
            Top Up
          </Button>
        </div>

        <PreBidForm publicKey={publicKey} />
      </div>

      {/* Hidden for now */}
      <div className="hidden">
        <div
          className="
            mt-6 w-full rounded-2xl border text-center text-xs text-muted
          "
        >
          <div className="flex flex-col gap-2">
            <div
              className="
                flex h-[140px] items-center justify-center overflow-hidden
                rounded-t-2xl
                sm:h-[240px]
              "
              style={{
                WebkitMaskImage:
                  "linear-gradient(180deg, #000000FF 40%, #00000000 100%)",
              }}
            >
              <Image
                src="/assets/empty-40.png"
                alt="Get tokens early"
                width={700}
                height={0}
                className="h-auto"
              />
            </div>

            <div className="z-10 -mt-12 flex-col items-center gap-2 p-4 pb-8">
              <h3
                className="
                  mb-2 font-sans text-xl font-semibold text-muted-foreground
                  sm:text-2xl
                "
              >
                Get your tokens early!
              </h3>

              <p className="text-sm/5 font-normal text-balance text-muted">
                Before the market opens, anyone can place a bid to reserve
                tokens at the lowest possible price, including you! Bids will be
                filled by the Automated Market Maker's sell orders (aka the
                &quot;AMM&quot;) when the market opens.
              </p>

              <div className="text-sm text-balance text-muted opacity-50">
                Learn more about how the AMM works on{" "}
                <span
                  onClick={() => {
                    setActiveTab(USER_PROFILE_TAB_ITEMS_SUBCATEGORIES.AMM);
                  }}
                  className="cursor-pointer underline underline-offset-2"
                >
                  the AMM tab
                </span>
                .
              </div>
              <Button
                variant="default"
                size="sm"
                className="mt-4"
                onClick={() =>
                  setDialog(DIALOG.GetTokensEarly, {
                    open: true,
                    params: {
                      username: coinUserName || "",
                    },
                  })
                }
              >
                Get Your Tokens
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PreLaunchMetrics = ({
  totalBidsFocus,
  focusExchangeRate,
  totalBids,
  uniqueWallets,
  wrapperClassName,
}: {
  totalBidsFocus: number;
  focusExchangeRate: number;
  totalBids: number;
  uniqueWallets: string[];
  wrapperClassName?: string;
}) => {
  return (
    <div className={cn("flex flex-row gap-4", wrapperClassName)}>
      <Metric
        label="Total Bids"
        state="active"
        value={
          <CoinPriceFormatter
            className="text-2xl"
            price={focusToUsd(totalBidsFocus, focusExchangeRate)}
          />
        }
        caption={
          <>
            <CoinPriceFormatter price={totalBidsFocus} isUsd={false} />{" "}
            {FOCUS_TICKER_DISPLAY_NAME}
          </>
        }
        className="w-full border-border"
      />

      <Metric
        label="Number of Bids"
        caption={`${uniqueWallets.length} Users`}
        state="active"
        value={totalBids}
        className="w-full border-border"
      />
    </div>
  );
};

export const EarlyUniqueBidders = ({
  uniqueWallets,
  totalBidsUsd,
  username,
}: {
  username: string;
  totalBidsUsd: number;
  uniqueWallets: string[];
}) => {
  return (
    <div className="mt-4 flex flex-col items-center justify-center gap-2">
      <div className="flex -space-x-3">
        {uniqueWallets.slice(0, MAX_WALLETS_SHOWN).map((publicKey) => (
          <AccountSocialCard
            publicKey={publicKey}
            username={""}
            key={publicKey}
          >
            <div>
              <DesoAvatar
                key={publicKey}
                className="cursor-pointer border-2 border-background"
                size={32}
                clickable={false}
                account={{
                  // Note: no ability to pass extra data to the DesoAvatar component since bids have only transactor public key
                  // The social card component will preform account loading, so when you hover you'll see the account details,
                  // but the avatar can mismatch in the social card and here and this is expected.
                  publicKey: publicKey,
                }}
              />
            </div>
          </AccountSocialCard>
        ))}
      </div>

      <span className="mt-1 text-xs text-muted">
        {uniqueWallets.length ? (
          <>
            <span className="font-semibold underline underline-offset-2">
              {pluralize(uniqueWallets.length, "early user")}
            </span>{" "}
            {uniqueWallets.length === 1 ? "has" : "have"} bid{" "}
            <span className="font-mono text-emerald-400">
              {formatUSD(totalBidsUsd)}
            </span>{" "}
            so far.
          </>
        ) : (
          username && (
            <>
              No early bids yet! Be the first by placing a bid via{" "}
              <Link
                href={`${process.env.NEXT_PUBLIC_OPENFUND_URI}/trade/${username}?quote=${FOCUS_TOKEN_DISPLAY_NAME}`}
                target="_blank"
                className="underline underline-offset-4"
              >
                {OPENFUND_APP_NAME}
              </Link>
            </>
          )
        )}
      </span>
    </div>
  );
};
